import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Table, Button, Badge } from 'react-bootstrap';
import Breadcrumbs from '../../Common/Breadcrumbs';
import ShimmerLoader from '../../Common/ShimmerLoader';
import { TournamentForFixture, getAgeCategoryFixtures } from '../../api/fixtures/fixturesService';

interface AgeCategoryFixture {
  _id: string;
  ageCategory: string;
  startDate: string;
  tournamentType: 'Group + Knockout' | 'Direct Knockout';
  status: 'Upcoming' | 'Ongoing' | 'Completed';
}

const AgeCategoryListingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [ageCategoryFixtures, setAgeCategoryFixtures] = useState<AgeCategoryFixture[]>([]);
  const [tournament, setTournament] = useState<TournamentForFixture | null>(null);

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Dashboard', onClick: () => navigate('/dashboard') },
    { label: 'Manage Tournaments', onClick: () => navigate('/fixtures') },
    { label: tournament?.name || 'Age Categories', active: true }
  ];

  useEffect(() => {
    // Get tournament data from navigation state
    const tournamentData = location.state?.selectedTournament;
    if (tournamentData) {
      setTournament(tournamentData);
      loadAgeCategoryFixtures(tournamentData);
    } else {
      // If no tournament data, redirect back to fixtures listing
      navigate('/fixtures');
    }
  }, [location.state, navigate]);

  const loadAgeCategoryFixtures = async (tournamentData: TournamentForFixture) => {
    try {
      setLoading(true);
      
      // Get existing age category fixtures from API
      const existingFixtures = await getAgeCategoryFixtures(tournamentData._id);
      setAgeCategoryFixtures(existingFixtures);
    } catch (error) {
      console.error('Error loading age category fixtures:', error);
      // Fallback to empty array if API fails
      setAgeCategoryFixtures([]);
    } finally {
      setLoading(false);
    }
  };

  // Check if all age categories are created
  const getAvailableAgeCategories = () => {
    if (!tournament) return [];
    
    const createdCategories = ageCategoryFixtures.map(fixture => fixture.ageCategory);
    return tournament.ageCategories.filter(category => !createdCategories.includes(category));
  };

  const availableAgeCategories = getAvailableAgeCategories();
  const canAddMore = availableAgeCategories.length > 0;

  const handleAddFixture = () => {
    if (tournament) {
      navigate('/fixtures/age-category/add', {
        state: {
          selectedTournament: tournament
        }
      });
    }
  };

  const handleEditFixture = (fixture: AgeCategoryFixture) => {
    if (tournament) {
      navigate('/fixtures/age-category/edit', {
        state: {
          selectedTournament: tournament,
          selectedFixture: fixture
        }
      });
    }
  };

  const handleManageFixtures = (fixture: AgeCategoryFixture) => {
    // Navigate to fixtures management page with teams, groups, fixtures tabs
    navigate(`/fixtures/${fixture._id}`, {
      state: {
        selectedTournament: tournament,
        selectedFixture: fixture
      }
    });
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'upcoming': return 'primary';
      case 'ongoing': return 'success';
      case 'completed': return 'secondary';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          {/* Breadcrumbs */}
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Breadcrumbs items={breadcrumbItems} />
          </div>
          
          {/* Header - Outside white box like fixtures listing */}
          <div style={{ padding: '0 15px', marginBottom: '24px' }}>
            <div className="teams-header-section mb-4" style={{ position: 'relative', zIndex: 10, padding: '0', background: 'transparent', margin: '0', backgroundColor: 'transparent' }}>
              <div className="d-flex justify-content-between align-items-center" style={{ position: 'relative', zIndex: 10, width: '100%', padding: '0', background: 'transparent', backgroundColor: 'transparent' }}>
                <div className="teams-section-info" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: '0 0 auto', background: 'transparent', backgroundColor: 'transparent' }}>
                  <h4 className="section-title mb-2" style={{ fontSize: '20px', fontWeight: '600', color: '#495057', margin: '0 0 8px 0', lineHeight: '1.3' }}>Age Category List</h4>
                  <p className="section-description mb-0" style={{ fontSize: '14px', color: '#6c757d', margin: '0', lineHeight: '1.4' }}>Manage fixture types for each age category</p>
                </div>
                <div className="filter-controls-right" style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-end', marginTop: '0', flexWrap: 'nowrap' }}>
                  <div className="shimmer-button" style={{ width: '80px', height: '38px', borderRadius: '6px', background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }}></div>
                </div>
              </div>
            </div>
          </div>

          <Row>
            <Col>
              <div className="tournament-table-wrapper">
                <ShimmerLoader type="table" />
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>
        
        {/* Header - Outside white box like fixtures listing */}
        <div style={{ padding: '0 15px', marginBottom: '24px' }}>
          <div className="teams-header-section mb-4" style={{ position: 'relative', zIndex: 10, padding: '0', background: 'transparent', margin: '0', backgroundColor: 'transparent' }}>
            <div className="d-flex justify-content-between align-items-center" style={{ position: 'relative', zIndex: 10, width: '100%', padding: '0', background: 'transparent', backgroundColor: 'transparent' }}>
              <div className="teams-section-info" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: '0 0 auto', background: 'transparent', backgroundColor: 'transparent' }}>
                <div className="d-flex align-items-center mb-2">
                  <button 
                    className="back-button me-3"
                    onClick={() => navigate('/fixtures')}
                    style={{ 
                      background: 'none',
                      border: 'none',
                      color: '#6c757d',
                      fontSize: '18px',
                      cursor: 'pointer',
                      padding: '4px',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#495057';
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#6c757d';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title="Back to Fixtures"
                  >
                    <i className="ri-arrow-left-line"></i>
                  </button>
                  <h4 className="section-title mb-0" style={{ fontSize: '20px', fontWeight: '600', color: '#495057', margin: '0', lineHeight: '1.3' }}>Age Category List</h4>
                </div>
                <p className="section-description mb-0" style={{ fontSize: '14px', color: '#6c757d', margin: '0', lineHeight: '1.4' }}>Manage fixture types for each age category</p>
              </div>
              {canAddMore && (
                <div className="filter-controls-right" style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-end', marginTop: '0', flexWrap: 'nowrap' }}>
                  <button
                    className="btn btn-primary add-club-btn"
                    onClick={handleAddFixture}
                    style={{ 
                      position: 'relative', 
                      zIndex: 15,
                      fontFamily: 'Inter, Helvetica Neue, Arial, sans-serif',
                      fontWeight: 500,
                      fontSize: '13px',
                      lineHeight: '150%',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: '1px solid #2563eb',
                      backgroundColor: '#2563eb',
                      color: '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      textDecoration: 'none',
                      minWidth: 'auto',
                      height: '38px'
                    }}
                  >
                    + Add
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <Row>
          <Col>
            {/* Age Category Table - Inside white container */}
            <div className="tournament-table-wrapper">
              <Table responsive className="tournament-table">
                <thead>
                  <tr>
                    <th>Age Category</th>
                    <th>Start Date</th>
                    <th>Tournament Type</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ageCategoryFixtures.map((fixture) => (
                    <tr key={fixture._id}>
                      <td>
                        <div className="tournament-name">
                          <strong>{fixture.ageCategory}</strong>
                        </div>
                      </td>
                      <td>
                        <div className="tournament-date">
                          {fixture.startDate || '-'}
                        </div>
                      </td>
                      <td>
                        <div className="tournament-venue">
                          {fixture.tournamentType || '-'}
                        </div>
                      </td>
                      <td>
                        <Badge bg={getStatusBadgeVariant(fixture.status)}>
                          {fixture.status}
                        </Badge>
                      </td>
                      <td>
                        <div className="tournament-actions">
                          <button
                            className="action-btn btn-edit"
                            onClick={() => handleEditFixture(fixture)}
                            title="Edit Age Category"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button
                            className="action-btn btn-view"
                            onClick={() => handleManageFixtures(fixture)}
                            title="Manage Fixtures"
                          >
                            <i className="ri-calendar-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AgeCategoryListingPage;
